import React, { memo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * FocusModeWrapper component
 *
 * Provides consistent layout behavior for alignment session sections
 * in both normal and focus modes.
 *
 * In normal mode: transparent wrapper (no layout changes)
 * In focus mode: calculates available height dynamically and applies it
 *
 * The wrapper measures the parent container and calculates the exact
 * height available for content, ensuring cards end at the same position
 * as in normal mode.
 */
function FocusModeWrapper({ children, focusMode, topMargin = 30 }) {
  const wrapperRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  useEffect(() => {
    if (!focusMode || !wrapperRef.current) {
      setAvailableHeight(null);
      return;
    }

    const calculateHeight = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      // Find the focusModePageWrapper (direct parent container in focus mode)
      const pageWrapper = wrapper.closest('[class*="focusModePageWrapper"]');
      if (!pageWrapper) return;

      // Calculate available height as the distance between the wrapper's top
      // and the page wrapper's bottom. This accounts for any layout shifts
      // caused by different screen ratios.
      const pageWrapperRect = pageWrapper.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const calculated = pageWrapperRect.bottom - wrapperRect.top;

      setAvailableHeight(Math.max(calculated, 200)); // Minimum 200px
    };

    // Calculate on mount
    calculateHeight();

    // Recalculate on resize
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [focusMode, topMargin]);

  // In normal mode, render children directly without any wrapper
  if (!focusMode) {
    return <>{children}</>;
  }

  // Focus mode: apply calculated height with top margin
  const wrapperStyle = {
    height: availableHeight ? `${availableHeight}px` : "100%",
    marginTop: `${topMargin}px`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      {children}
    </div>
  );
}

FocusModeWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  focusMode: PropTypes.bool,
  topMargin: PropTypes.number,
};

FocusModeWrapper.defaultProps = {
  focusMode: false,
  topMargin: 30,
};

export default memo(FocusModeWrapper);
